import React, { useEffect, useRef, useState } from "react";
import { LiveDot } from "./ui";

/* Animated WhatsApp chat — drop-in replacement for the static prototype's WA component. */

const WA_SCRIPT = [
  { who: "agent",    t: "09:02", text: "Assalamu alaikum Karim Bhai 👋\nWelcome to Hossain Trading on WhatsApp." },
  { who: "agent",    t: "09:02", text: "How can I help today? You can ask in Bangla or English." },
  { who: "customer", t: "09:03", text: "Pusti soybean oil 5L ar dam koto ekhon?" },
  { who: "agent",    t: "09:03", text: "Today's price for you:\n\n🛢️ *Pusti Soybean 5L* — ৳ 950 / can\n🛢️ *Rupchanda 5L* — ৳ 980 / can\n\nYou usually take Pusti — 24 cans last month. Want to reorder?" },
  { who: "customer", t: "09:04", text: "hae 30 can pathao" },
  { who: "agent",    t: "09:04", text: "Got it ✅\n\n*30 × Pusti Soybean 5L*\nSubtotal: *৳ 28,500*\nCredit limit: ৳ 40,000 (avail ৳ 32,400)\nTerms: 30-day · delivery tomorrow 11 am\n\nConfirm order?" },
  { who: "customer", t: "09:05", text: "confirm" },
  { who: "agent",    t: "09:05", text: "✅ *Order #BD-49281 confirmed*\n\n📦 30× Pusti Soybean 5L\n🚚 Delivery: tomorrow 11:00 am\n💰 ৳ 28,500 · due 20 Jun\n\nI'll ping you when the truck leaves the DC. Salaam!" },
  { who: "customer", t: "09:06", text: "next time order easier kora jabe?" },
  { who: "agent",    t: "09:06", text: "Of course — next time just type *\"order soybean 30\"* and I'll do it in one step. Or send me a voice note in Bangla, I understand 🙏" },
];

const renderWAText = (text) => {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((p, i) =>
    p.startsWith("*") && p.endsWith("*") && p.length > 2
      ? <strong key={i} className="font-semibold text-white">{p.slice(1, -1)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
};

const Bubble = ({ msg }) => {
  const isAgent = msg.who === "agent";
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"} fadeup`}>
      <div className={`max-w-[78%] rounded-lg px-3 py-2 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] ${isAgent ? "bg-[#202C33] rounded-tl-sm" : "bg-[#005C4B] rounded-tr-sm"}`}>
        <div className="whitespace-pre-wrap text-[13.5px] leading-[1.35] text-white/95">{renderWAText(msg.text)}</div>
        <div className="mt-0.5 flex items-center justify-end gap-1">
          <span className="text-[10px] text-white/45">{msg.t}</span>
          {!isAgent && (
            <svg className="h-3 w-3.5 text-[#53BDEB]" viewBox="0 0 18 11" fill="currentColor">
              <path d="M11.071 0.653a.5.5 0 01.123.696l-4.5 6.5a.5.5 0 01-.78.054L3.146 5.146a.5.5 0 01.708-.708L6.27 6.854 10.375.776a.5.5 0 01.696-.123zm5 0a.5.5 0 01.123.696l-4.5 6.5a.5.5 0 01-.78.054l-.92-.92.708-.708.605.605L15.375.776a.5.5 0 01.696-.123z"/>
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};

const Typing = () => (
  <div className="flex justify-start fadeup">
    <div className="rounded-lg rounded-tl-sm bg-[#202C33] px-3 py-2.5 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)]">
      <div className="flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-white/60 wa-bounce" style={{ animationDelay: "0ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-white/60 wa-bounce" style={{ animationDelay: "140ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-white/60 wa-bounce" style={{ animationDelay: "280ms" }} />
      </div>
    </div>
  </div>
);

export default function WhatsAppDemo({ height = 540, replay = true, autoStart = true }) {
  const [step, setStep] = useState(0);
  const [typing, setTyping] = useState(false);
  const [paused, setPaused] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (paused || !autoStart) return;
    if (step >= WA_SCRIPT.length) {
      if (!replay) return;
      const t = setTimeout(() => setStep(0), 4500);
      return () => clearTimeout(t);
    }
    const next = WA_SCRIPT[step];
    const typingDelay = next.who === "agent" ? 900 : 600;
    let t1, t2;
    if (step > 0) {
      setTyping(true);
      t1 = setTimeout(() => {
        setTyping(false);
        t2 = setTimeout(() => setStep(s => s + 1), 80);
      }, typingDelay);
    } else {
      t2 = setTimeout(() => setStep(s => s + 1), 400);
    }
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [step, paused, replay, autoStart]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [step, typing]);

  return (
    <div className="relative">
      <div className="rounded-[28px] border border-white/[0.08] bg-[#0a0f15] p-2 shadow-2xl shadow-black/40">
        <div className="flex items-center justify-between rounded-t-[22px] bg-[#202C33] px-3 py-2.5">
          <div className="flex items-center gap-3">
            <button className="text-white/70" aria-label="Back">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-white">
              <img src="/assets/images/ekkbaz-mark.png" alt="" className="h-7 w-7 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-[13.5px] font-semibold text-white">Hossain Trading</div>
              <div className="flex items-center gap-1.5 text-[11px] text-white/55">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                online · AI-assisted
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-white/60">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"/></svg>
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.97.97 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/></svg>
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
          </div>
        </div>

        <div ref={bodyRef} className="relative overflow-y-auto px-3 py-4 wa-bg" style={{ height: height + "px" }}>
          <div className="mb-4 flex justify-center">
            <span className="rounded-md bg-[#182229] px-2.5 py-1 text-[10.5px] font-medium text-white/70">TODAY</span>
          </div>
          <div className="flex flex-col gap-1.5">
            {WA_SCRIPT.slice(0, step).map((m, i) => <Bubble key={i} msg={m} />)}
            {typing && step < WA_SCRIPT.length && WA_SCRIPT[step].who === "agent" && <Typing />}
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-b-[22px] bg-[#202C33] px-3 py-2">
          <svg className="h-5 w-5 text-white/50" viewBox="0 0 24 24" fill="currentColor"><path d="M9.153 11.603c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zm-3.204 1.362c-.026-.307-.131 5.218 6.063 5.551 6.066-.25 6.066-5.551 6.066-5.551-6.078 1.416-12.129 0-12.129 0zM11.804 1.011C5.609 1.011.978 6.033.978 12.228s4.826 10.761 11.021 10.761S23.02 18.423 23.02 12.228c.001-6.195-5.021-11.217-11.216-11.217z"/></svg>
          <div className="flex flex-1 items-center gap-2 rounded-full bg-[#2A3942] px-3 py-2">
            <span className="text-[12.5px] text-white/40">Message</span>
          </div>
          <svg className="h-5 w-5 text-white/50" viewBox="0 0 24 24" fill="currentColor"><path d="M1.101 21.757L23.8 12.028 1.101 2.3l.012 7.912 13.623 1.816-13.623 1.817-.012 7.912z"/></svg>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">
          <LiveDot /> &nbsp;live WhatsApp · session bd_4928
        </div>
        <button onClick={() => setPaused(p => !p)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white">
          {paused ? "▶ resume" : "⏸ pause"}
        </button>
      </div>
    </div>
  );
}
